// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unused-expressions, import/named, react-hooks/exhaustive-deps, react-hooks/use-memo */

import React, { FC, useState, useEffect, useMemo, createRef, useCallback } from "react";
import { useAuth0 } from "../../../../auth/auth0";
import { useUserAuth } from "../../../../hooks/useUserAuth";
import { useMutation } from "@tanstack/react-query";
import { Button, Table, Icon, Modal, Input, Dropdown, DropdownItemProps, Loader } from "semantic-ui-react";
import * as factorApi from "../../../../apis/factorApi";
import _ from "lodash";
import { toast } from "react-toastify";
// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
import { CSVLink } from "react-csv";
import "./style.css";
import Pagination from "../../../Lumber/Pagination";
import {
	ExperimentalModalInfo,
	FieldOutline,
	Validation,
	ExperimentalDesignFactor,
} from "../../../../apis/types/factorTypes";

type Props = {
	typeOptions: DropdownItemProps[];
	experimentalModalInfo: ExperimentalModalInfo;
	setExperimentalModalInfo: React.Dispatch<React.SetStateAction<ExperimentalModalInfo>>;
	experimentalDesigns: ExperimentalDesignFactor[];
	setExperimentalDesigns: React.Dispatch<React.SetStateAction<ExperimentalDesignFactor[]>>;
	fieldOutline: FieldOutline[];
	fieldId: string;
	canEdit: boolean;
};

type CsvDataWithPlot = {
	plotId?: string;
	Field: string;
	Range: number;
	Column: number;
	Company: string | null;
	Trial: string | null;
	Treatment: number | null;
	"Treatment Name": string | null;
	"Treatment Block Id": string | null;
	"Replicate Id": number | null;
	Seed: string | null;
	"Rec Id": string | null;
};

const ExperimentalDesignModal: FC<Props> = ({
	typeOptions,
	experimentalModalInfo,
	setExperimentalModalInfo,
	experimentalDesigns,
	setExperimentalDesigns,
	fieldOutline,
	fieldId,
	canEdit,
}) => {
	const userAuth = useUserAuth();
	const { getTokenSilently } = useAuth0();

	const [csvData, setCsvData] = useState<any>();
	const [expDesignFile, setExpDesignFile] = useState<File | null>(null);
	const [inspectingFile, setInspectingFile] = useState<boolean>(false);
	const [inspectionResults, setInspectionResults] = useState<Validation[]>([]);
	const [inspectionFailed, setInspectionFailed] = useState<boolean>(false);
	const [factorNameUpdated, setFactorNameUpdated] = useState<boolean>(false);
	const [factorNameInput, setFactorNameInput] = useState<string>("");
	const [plotsUpdated, setPlotsUpdated] = useState<boolean>(false);
	const [typeUpdated, setTypeUpdated] = useState<boolean>(false);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [showOnlyErrors, setShowOnlyErrors] = useState<boolean>(false);

	const [pageSize, setPageSize] = useState<10 | 25 | 50 | 100>(100);
	const [pageNumber, setPageNumber] = useState(1);

	const fileInputRef = createRef<HTMLInputElement>();

	const maxPage = useMemo(() => {
		return fieldOutline
			? Math.ceil(
					showOnlyErrors
						? _.filter(fieldOutline, (plot) =>
								_.some(inspectionResults, (ir) => ir.plotId === plot.plotId && ir.severity === 3),
							).length
						: fieldOutline.length / pageSize,
				)
			: 1;
	}, [fieldOutline, pageSize, showOnlyErrors, inspectionResults]);
	const paginatedData = useMemo(() => {
		return showOnlyErrors
			? _.filter(fieldOutline, (plot) =>
					_.some(inspectionResults, (ir) => ir.plotId === plot.plotId && ir.severity === 3),
				)
			: fieldOutline?.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
	}, [fieldOutline, pageSize, pageNumber, showOnlyErrors, inspectionResults]);

	useEffect(() => {
		if (experimentalModalInfo?.open) {
			setFactorNameInput(experimentalModalInfo.factorName ?? "");
		}
	}, [experimentalModalInfo?.open, experimentalModalInfo.factorName]);

	useEffect(() => {
		if (experimentalModalInfo && fieldOutline) {
			let partialCsv: Partial<CsvDataWithPlot>[] = _.map(fieldOutline, (plot) => ({
				plotId: plot.plotId,
				Field: plot.field,
				Range: plot.range,
				Column: plot.column,
				Company: plot.company,
				Trial: plot.trial,
				Treatment: null,
				"Treatment Name": null,
				"Treatment Block Id": null,
				"Replicate Id": null,
				Seed: null,
				"Rec Id": null,
			}));

			partialCsv = _.map(partialCsv, (plot) => {
				const matchingPlot = _.find(experimentalModalInfo.plots, (p) => p.plotId === plot.plotId);

				if (matchingPlot) {
					plot["Treatment Block Id"] = matchingPlot.plotGroupingDisplayId ?? null;
					plot["Treatment Name"] = matchingPlot.treatmentName ?? "";
					plot["Treatment"] = matchingPlot.treatmentDisplayId ?? null;
					plot["Replicate Id"] = matchingPlot.replicate ?? null;
					plot["Seed"] = matchingPlot.seed ?? null;
					plot["Rec Id"] = matchingPlot.recId ?? null;
				}

				return plot;
			});

			_.forEach(partialCsv, (p) => delete p.plotId);

			setCsvData(partialCsv);
		}
	}, [experimentalModalInfo, fieldOutline]);

	const handleFileBrowse = () => {
		fileInputRef.current?.click();
	};

	function fileSelectedHandler(event: React.ChangeEvent<HTMLInputElement>) {
		event.persist();

		if (!event.target.files) return;
		else {
			const expDesignLayout = event.target.files[0];
			setInspectingFile(true);
			setExpDesignFile(expDesignLayout);
			inspectFile(expDesignLayout);
		}
	}

	const fileRemovedHandler = () => {
		const uploadElement = document.getElementById("fieldLayoutUpload") as HTMLInputElement;
		if (uploadElement) {
			uploadElement.files = null;
			uploadElement.value = "";
			setExpDesignFile(null);
		}
	};

	const handleClose = () => {
		fileRemovedHandler();
		setInspectingFile(false);
		setInspectionResults([]);
	};

	const inspectExpDesignFile = async (file: File) => {
		const accessToken = await getTokenSilently();
		return factorApi.inspectExperimentalDesignFile(
			file,
			userAuth.currentClientId!,
			fieldId,
			experimentalModalInfo.designId,
			accessToken,
		);
	};

	const { mutate: inspectFile } = useMutation({
		mutationFn: (file: File) => inspectExpDesignFile(file),
		onSuccess: (data) => {
			if (data.data.validations.some((x) => x.severity === 3)) {
				toast.error("Experimental Design Layout has errors in it");
				setInspectionFailed(true);
			} else {
				toast.success("Experimental Design Layout accepted");
				setInspectionFailed(false);
			}

			setInspectingFile(false);
			setPlotsUpdated(true);
			setShowOnlyErrors(false);
			setExperimentalModalInfo((modal) => ({ ...modal, plots: data.data.factorPlots }));
			setInspectionResults(data.data.validations);
		},
		onError: (error) => {
			toast.error("Experimental Design Layout failed validation");
			setInspectingFile(false);
			console.error(error);
		},
	});

	const createExperimentalDesignMutationFn = async () => {
		setIsSaving(true);
		const accessToken = await getTokenSilently();
		return factorApi.createExperimentalDesign(experimentalModalInfo, fieldId, userAuth.currentClientId!, accessToken);
	};

	const { mutate: createExperimentalDesign } = useMutation({
		mutationFn: () => createExperimentalDesignMutationFn(),
		onSuccess: (data) => {
			if (data.statusCode !== 200) {
				toast.error(data.messages[0]);
			} else {
				toast.success("Experimental Design created successfully!");
				setExperimentalModalInfo((modal) => ({ ...modal, designId: data.data.id, open: false }));

				//-- Cleanup if default design is getting set here (first creation)
				if (experimentalModalInfo.default) {
					cleanupPreviousDefault(data.data);
				} else {
					updateDesignList(data.data);
				}

				handleClose();
			}

			setIsSaving(false);
		},
		onError: (error) => {
			toast.error(
				"Failed to create experimental design. Please try again later or submit a ticket if this keeps happening",
			);
			console.error(error);
			setIsSaving(false);
		},
	});

	const editExperimentalDesignMutationFn = async () => {
		setIsSaving(true);
		const accessToken = await getTokenSilently();
		return factorApi.editExperimentalDesign(experimentalModalInfo, fieldId, userAuth.currentClientId!, accessToken);
	};

	const { mutate: editExperimentalDesign } = useMutation({
		mutationFn: () => editExperimentalDesignMutationFn(),
		onSuccess: (data) => {
			if (data.statusCode !== 200) {
				toast.error("Failed to update Experimental Design");
				console.error(data.data);
			} else {
				toast.success("Experimental Design edited successfully!");
				updateDesignList(data.data);
				handleClose();
				setExperimentalModalInfo((modal) => ({ ...modal, open: false }));
			}

			setIsSaving(false);
		},
		onError: (error) => {
			toast.error(
				"Failed to edit experimental design. Please try again later or submit a ticket if this keeps happening",
			);
			console.error(error);
			setIsSaving(false);
		},
	});

	const handleSave = () => {
		if (
			_.some(
				experimentalDesigns,
				(ex) => ex.name === experimentalModalInfo.factorName && ex.id !== experimentalModalInfo.designId,
			)
		) {
			toast.error("Factor with this name already exists on field. Check factors that are not fully deleted yet");
		} else {
			experimentalModalInfo.designId ? editExperimentalDesign() : createExperimentalDesign();
		}
	};

	const updateDesignList = (expDesign: ExperimentalDesignFactor) => {
		expDesign.factorTreatments = _.sortBy(expDesign.factorTreatments, (t) => t.displayId);
		expDesign.default = experimentalModalInfo.default;

		let experimentalDesignsClone = _.cloneDeep(experimentalDesigns);
		experimentalDesignsClone = findAndReplaceExpDesign(experimentalDesignsClone, expDesign);

		setExperimentalDesigns(experimentalDesignsClone);
	};

	//-- Tries to find matching experimental design and replaces with incoming one
	//-- If none found with that Id, will add to list instead
	const findAndReplaceExpDesign = (expDesignList: ExperimentalDesignFactor[], expDesign: ExperimentalDesignFactor) => {
		if (_.some(expDesignList, (ed) => ed.id === expDesign.id)) {
			const matchingIndex = _.findIndex(expDesignList, (ed) => ed.id === expDesign.id);

			expDesignList[matchingIndex] = expDesign;
		} else {
			expDesignList.push(expDesign);
		}

		return expDesignList;
	};

	const cleanupPreviousDefault = (newExpDesign: ExperimentalDesignFactor) => {
		let experimentalDesignsClone = _.cloneDeep(experimentalDesigns);
		let defaultExpDesign = _.find(experimentalDesigns, { default: true });

		if (defaultExpDesign) {
			defaultExpDesign = { ...defaultExpDesign, default: false };
			experimentalDesignsClone = findAndReplaceExpDesign(experimentalDesignsClone, defaultExpDesign);

			editExperimentalDesignInfo([defaultExpDesign]);
		}

		experimentalDesignsClone = findAndReplaceExpDesign(experimentalDesignsClone, newExpDesign);
		setExperimentalDesigns(experimentalDesignsClone);
	};

	const editExperimentalDesignInfoFn = async (expDesigns: ExperimentalDesignFactor[]) => {
		const accessToken = await getTokenSilently();
		return factorApi.editExperimentalDesignInfo(expDesigns, userAuth.currentClientId!, accessToken);
	};

	const { mutate: editExperimentalDesignInfo } = useMutation({
		mutationFn: (expDesigns: ExperimentalDesignFactor[]) => editExperimentalDesignInfoFn(expDesigns),
		onSuccess: (data) => {
			if (data.statusCode !== 200) {
				toast.error("Failed to update factor info");
			}
		},
	});

	const debouncedFactorNameUpdate = useCallback(
		_.debounce((factorNameChange) => {
			setExperimentalModalInfo((modal) => ({ ...modal, factorName: factorNameChange }));
			setFactorNameUpdated(true);
		}, 300),
		[],
	);

	return (
		<>
			<Modal open={experimentalModalInfo.open} style={{ height: "90dvh" }}>
				<Modal.Header>{experimentalModalInfo.designId ? "Edit" : "Add"} Experimental Design Factor</Modal.Header>
				<Modal.Content className="experimentalDesignModal">
					<div className="modalLabel" style={{ gridArea: "designIdLabel", marginBottom: "-15px" }}>
						Design ID
					</div>
					<div style={{ gridArea: "designIdText", marginBottom: "-15px" }}>{experimentalModalInfo.displayId}</div>

					<div className="modalLabel" style={{ gridArea: "designNameLabel" }}>
						Design Name
					</div>
					<Input
						style={{ gridArea: "designNameText" }}
						value={factorNameInput}
						disabled={!canEdit}
						onChange={(e, { value }) => {
							setFactorNameInput(value);
							debouncedFactorNameUpdate(value);
						}}
					/>

					<div className="modalLabel" style={{ gridArea: "designCategoryLabel" }}>
						Design Category
					</div>
					<Dropdown
						style={{ gridArea: "designCategoryDdl" }}
						className="tableDropdown"
						disabled={!canEdit}
						options={typeOptions}
						value={experimentalModalInfo.typeId}
						onChange={(event, { value }) => {
							setExperimentalModalInfo((modal) => ({ ...modal, typeId: value as string }));
							setTypeUpdated(true);
						}}
					/>

					<div style={{ gridArea: "buttons", marginBottom: "-12px" }}>
						<Button
							positive
							floated="left"
							style={{ width: "48%" }}
							disabled={
								!canEdit ||
								isSaving ||
								inspectionFailed ||
								inspectingFile ||
								(!experimentalModalInfo.designId &&
									(!experimentalModalInfo.plots || !experimentalModalInfo.factorName)) ||
								(!!experimentalModalInfo.designId && !plotsUpdated && !factorNameUpdated && !typeUpdated)
							}
							loading={isSaving}
							onClick={handleSave}
						>
							Save
						</Button>
						<Button
							floated="right"
							style={{ width: "48%" }}
							disabled={isSaving}
							onClick={() => {
								setExperimentalModalInfo((modal) => ({ ...modal, open: false }));
								handleClose();
							}}
						>
							Cancel
						</Button>
					</div>

					<CSVLink
						style={{ gridArea: "uploadLabel" }}
						filename={`${fieldOutline[0]?.field} - ${experimentalModalInfo.displayId}`}
						data={csvData}
						enclosingCharacter={""}
					>
						Current Design Layout
					</CSVLink>
					<Input type="text" action readOnly value={expDesignFile?.name ?? ""} style={{ gridArea: "uploadInput" }}>
						<Button
							content="Browse..."
							labelPosition="left"
							icon="folder open solid"
							disabled={!canEdit || inspectingFile}
							style={{ borderTopRightRadius: "unset", borderBottomRightRadius: "unset" }}
							onClick={() => {
								handleFileBrowse();
							}}
						/>
						<input style={{ borderTopLeftRadius: "unset", borderBottomLeftRadius: "unset" }} />
						<Button icon disabled={!expDesignFile || inspectingFile} onClick={fileRemovedHandler}>
							{inspectingFile ? <Loader active inline size="tiny" /> : <Icon className="trash solid" />}
						</Button>
					</Input>
					<input
						id="fieldLayoutUpload"
						ref={fileInputRef}
						type="file"
						accept=".csv"
						hidden
						onChange={fileSelectedHandler}
					/>

					<div style={{ gridArea: "inspection", height: "100%", position: "relative" }}>
						{inspectionResults.length !== 0 && (
							<>
								{_.some(inspectionResults, (ir) => ir.severity === 3) && (
									<div
										style={{
											right: 0,
											position: "absolute",
											bottom: `${
												(_.uniqBy(
													_.filter(inspectionResults, (ir) => ir.severity === 3),
													(ir) => ir.message,
												).length +
													3) * //-- Adding 3 places of buffer, not exact math
												15
											}px`,
										}}
									>
										<Button style={{ margin: 0 }} compact onClick={() => setShowOnlyErrors((prev) => !prev)}>
											{showOnlyErrors ? "Show all plots" : "Show plots with errors"}
										</Button>
									</div>
								)}
								<div className="inspectionResults">
									{_.map(
										_.uniqBy(
											_.filter(inspectionResults, (ir) => ir.severity === 3),
											(ir) => ir.message,
										),
										(ir, i) => {
											const inspectionLength = _.uniqBy(
												_.filter(inspectionResults, (ir) => ir.severity === 3),
												(ir) => ir.message,
											).length;

											const styling = {
												padding:
													inspectionLength === 1
														? "15px"
														: i === 0
															? "15px 15px 0"
															: i === inspectionLength - 1
																? "0 15px 15px"
																: "0 15px",
											};

											return <div style={{ ...styling }}>{ir.message}</div>;
										},
									)}
								</div>
							</>
						)}
					</div>

					<div style={{ gridArea: "designTable", maxHeight: "100%", overflowY: "auto" }}>
						<Table>
							<Table.Header style={{ position: "sticky", top: 0 }}>
								<Table.HeaderCell>Field</Table.HeaderCell>
								<Table.HeaderCell>Range</Table.HeaderCell>
								<Table.HeaderCell>Column</Table.HeaderCell>
								<Table.HeaderCell>Company</Table.HeaderCell>
								<Table.HeaderCell>Trial</Table.HeaderCell>
								<Table.HeaderCell>Treatment</Table.HeaderCell>
								<Table.HeaderCell>Treatment Name</Table.HeaderCell>
								<Table.HeaderCell>Treatment Block Id</Table.HeaderCell>
								<Table.HeaderCell>Replicate Id</Table.HeaderCell>
							</Table.Header>
							<Table.Body>
								{_.map(paginatedData, (plot) => {
									const matchingPlot = _.find(experimentalModalInfo.plots, (p) => p.plotId === plot.plotId);

									//-- Inspection results for updates
									const treatmentNameUpdate = _.find(
										inspectionResults,
										(ir) =>
											ir.plotId === plot.plotId && ir.columnNames?.includes("Treatment Name") && ir.severity === 2,
									);
									const treatmentDisplayIdUpdate = _.find(
										inspectionResults,
										(ir) => ir.plotId === plot.plotId && ir.columnNames?.includes("Treatment") && ir.severity === 2,
									);
									const plotGroupingDisplayIdUpdate = _.find(
										inspectionResults,
										(ir) =>
											ir.plotId === plot.plotId && ir.columnNames?.includes("Plot Grouping Id") && ir.severity === 2,
									);
									const replicateUpdate = _.find(
										inspectionResults,
										(ir) => ir.plotId === plot.plotId && ir.columnNames?.includes("Replicate Id") && ir.severity === 2,
									);

									//-- Inspection results for errors
									const treatmentNameError = _.find(
										inspectionResults,
										(ir) =>
											ir.plotId === plot.plotId && ir.columnNames?.includes("Treatment Name") && ir.severity === 3,
									);
									const treatmentDisplayIdError = _.find(
										inspectionResults,
										(ir) => ir.plotId === plot.plotId && ir.columnNames?.includes("Treatment") && ir.severity === 3,
									);
									const plotGroupingDisplayIdError = _.find(
										inspectionResults,
										(ir) =>
											ir.plotId === plot.plotId && ir.columnNames?.includes("Plot Grouping Id") && ir.severity === 3,
									);
									const replicateError = _.find(
										inspectionResults,
										(ir) => ir.plotId === plot.plotId && ir.columnNames?.includes("Replicate Id") && ir.severity === 3,
									);

									return (
										<Table.Row key={plot.plotId}>
											<Table.Cell className="tableCell">{plot.field}</Table.Cell>
											<Table.Cell className="tableCell">{plot.range}</Table.Cell>
											<Table.Cell className="tableCell">{plot.column}</Table.Cell>
											<Table.Cell className="tableCell">{plot.company}</Table.Cell>
											<Table.Cell className="tableCell">{plot.trial}</Table.Cell>
											<Table.Cell
												className={
													"tableCell " +
													(treatmentDisplayIdError
														? "inspectionError"
														: treatmentDisplayIdUpdate
															? "inspectionWarning"
															: "")
												}
											>
												{matchingPlot?.treatmentDisplayId ?? ""}
											</Table.Cell>
											<Table.Cell
												className={
													"tableCell " +
													(treatmentNameError ? "inspectionError" : treatmentNameUpdate ? "inspectionWarning" : "")
												}
											>
												{matchingPlot?.treatmentName ?? ""}
											</Table.Cell>
											<Table.Cell
												className={
													"tableCell " +
													(plotGroupingDisplayIdError
														? "inspectionError"
														: plotGroupingDisplayIdUpdate
															? "inspectionWarning"
															: "")
												}
											>
												{matchingPlot?.plotGroupingDisplayId ?? ""}
											</Table.Cell>
											<Table.Cell
												className={
													"tableCell " +
													(replicateError ? "inspectionError" : replicateUpdate ? "inspectionWarning" : "")
												}
											>
												{matchingPlot?.replicate ?? ""}
											</Table.Cell>
										</Table.Row>
									);
								})}
							</Table.Body>
						</Table>
					</div>
					<Pagination
						style={{ gridArea: "pagination" }}
						displayItemCount={false}
						pageNumber={pageNumber}
						setPageNumber={setPageNumber}
						maxPage={maxPage}
						pageSize={pageSize}
						// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
						setPageSize={setPageSize}
					/>
				</Modal.Content>
			</Modal>
		</>
	);
};

export default ExperimentalDesignModal;
